"use strict";
// Copyright (c) 2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tx = exports.DEFAULT_TX_VERSION = exports.DEFAULT_SEQUENCE = void 0;
exports.readTxOutput = readTxOutput;
exports.writeOutPoint = writeOutPoint;
exports.writeTxInput = writeTxInput;
exports.writeTxOutput = writeTxOutput;
exports.copyTxInput = copyTxInput;
exports.copyTxOutput = copyTxOutput;
const bytes_js_1 = require("./io/bytes.js");
const hex_js_1 = require("./io/hex.js");
const varsize_js_1 = require("./io/varsize.js");
const writerbytes_js_1 = require("./io/writerbytes.js");
const writerlength_js_1 = require("./io/writerlength.js");
const ecc_js_1 = require("./ecc.js");
const script_js_1 = require("./script.js");
const hash_js_1 = require("./hash.js");
const signatories_js_1 = require("./signatories.js");
const sigHashType_js_1 = require("./sigHashType.js");
const unsignedTx_js_1 = require("./unsignedTx.js");
/**
 * Default value for nSequence of inputs if left undefined; this opts out of
 * BIP68 relative lock-time, and if all inputs have this value, nLockTime is
 * disabled, too.
 *
 * This is chosen as the default as it's the default in the node too,
 * see CTxIn in /src/primitives/transaction.h.
 **/
exports.DEFAULT_SEQUENCE = 0xffffffff;
/** Current tx version, see CTransaction in /stc/primitives/transaction.h */
exports.DEFAULT_TX_VERSION = 2;
/** CTransaction, a Bitcoin transaction. */
class Tx {
    constructor(params) {
        this.version = params?.version ?? exports.DEFAULT_TX_VERSION;
        this.inputs = params?.inputs ?? [];
        this.outputs = params?.outputs ?? [];
        this.locktime = params?.locktime ?? 0;
    }
    /** Serialize the tx to a byte array */
    ser() {
        const writerBytes = new writerbytes_js_1.WriterBytes(this.serSize());
        this.write(writerBytes);
        return writerBytes.data;
    }
    /** Serialize the tx to a hex string */
    toHex() {
        return (0, hex_js_1.toHex)(this.ser());
    }
    /** Calculate the serialized size of the tx */
    serSize() {
        const writerLength = new writerlength_js_1.WriterLength();
        this.write(writerLength);
        return writerLength.length;
    }
    /** Write the tx to the given writer */
    write(writer) {
        writer.putU32(this.version);
        (0, varsize_js_1.writeVarSize)(this.inputs.length, writer);
        for (const input of this.inputs) {
            writeTxInput(input, writer);
        }
        (0, varsize_js_1.writeVarSize)(this.outputs.length, writer);
        for (const output of this.outputs) {
            writeTxOutput(output, writer);
        }
        writer.putU32(this.locktime);
    }
    /** Deserialize a Tx from a Uint8Array */
    static deser(data) {
        const bytes = new bytes_js_1.Bytes(data);
        const version = bytes.readU32();
        const numInputs = (0, varsize_js_1.readVarSize)(bytes);
        const inputs = [];
        for (let i = 0; i < numInputs; ++i) {
            // Read OutPoint
            const txid = bytes.readBytes(32);
            const outIdx = bytes.readU32();
            // Read script
            const script = script_js_1.Script.readWithSize(bytes);
            // Read sequence
            const sequence = bytes.readU32();
            inputs.push({
                prevOut: {
                    txid,
                    outIdx,
                },
                script,
                sequence,
            });
        }
        const numOutputs = (0, varsize_js_1.readVarSize)(bytes);
        const outputs = [];
        for (let i = 0; i < numOutputs; ++i) {
            outputs.push(readTxOutput(bytes));
        }
        const locktime = bytes.readU32();
        return new Tx({
            version,
            inputs,
            outputs,
            locktime,
        });
    }
    /** Deserialize a Tx from a hex string */
    static fromHex(hex) {
        return Tx.deser((0, hex_js_1.fromHex)(hex));
    }
    /**
     * Compute the transaction ID (TxId) as a hex string (little-endian).
     * This follows the eCash convention: the TxId is the double SHA256 of the
     * serialized transaction, returned as a hex string in little-endian (reversed) order.
     * See the node src/primitives/txid.h for more details.
     */
    txid() {
        return (0, hex_js_1.toHexRev)((0, hash_js_1.sha256d)(this.ser()));
    }
    /**
     * Attempt to parse a **non-SegWit** serialized transaction from `data` — the same
     * encoding as {@link Tx.deser} / `Tx.ser()` (version, inputs, outputs, locktime;
     * no witness marker or witness stacks). eCash does not use SegWit transactions,
     * so this is the full-transaction wire format on-chain here. Returns a `Tx` only
     * when `data` is **exactly** one such transaction: the parse must consume the
     * **entire** buffer (no trailing bytes). Returns `undefined` on malformed input
     * or if any bytes remain after `locktime`.
     *
     * **PSBT-only motivation:** Bitcoin ABC’s PSBT input key `0x00` (`PSBT_IN_UTXO`)
     * stores **either** a full previous transaction (BIP 174 “non-witness UTXO”) **or**
     * a compact `CTxOut` (amount + `scriptPubKey`). Callers must disambiguate. Plain
     * {@link Tx.deser} reads a tx from the start of `data` but **does not** require
     * `data.length` to match the serialized length — leftover bytes are ignored, so
     * you cannot use it to prove “this blob is solely a full tx.” This helper is
     * used from PSBT (`resolveWitnessFromKey00` in `psbt.ts`): if `tryDeserExact`
     * succeeds (and the txid matches), treat as non-witness UTXO; otherwise decode as
     * `CTxOut`-shaped bytes.
     */
    static tryDeserExact(data) {
        try {
            const bytes = new bytes_js_1.Bytes(data);
            const version = bytes.readU32();
            const numInputs = (0, varsize_js_1.readVarSize)(bytes);
            const inputs = [];
            for (let i = 0; i < numInputs; ++i) {
                const txid = bytes.readBytes(32);
                const outIdx = bytes.readU32();
                const script = script_js_1.Script.readWithSize(bytes);
                const sequence = bytes.readU32();
                inputs.push({
                    prevOut: { txid, outIdx },
                    script,
                    sequence,
                });
            }
            const numOutputs = (0, varsize_js_1.readVarSize)(bytes);
            const outputs = [];
            for (let i = 0; i < numOutputs; ++i) {
                outputs.push(readTxOutput(bytes));
            }
            const locktime = bytes.readU32();
            if (bytes.idx !== data.length) {
                return undefined;
            }
            return new Tx({ version, inputs, outputs, locktime });
        }
        catch {
            return undefined;
        }
    }
    /**
     * Add a signature to a partially-signed multisig input.
     * Verifies the signature against the sighash for each pubkey in the
     * redeem/output script and merges with existing signatures (which pubkey
     * signed is inferred from verification).
     */
    addMultisigSignature(params) {
        const { inputIdx, signature, signData } = params;
        const ecc = params.ecc ?? new ecc_js_1.Ecc();
        const input = this.inputs[inputIdx];
        if (!input.script || input.script.bytecode.length === 0) {
            throw new Error(`Input ${inputIdx} has no scriptSig to add signature to`);
        }
        const isBare = signData.outputScript !== undefined &&
            signData.redeemScript === undefined;
        const parsed = isBare
            ? input.script.parseBareMultisigSpend(signData.outputScript)
            : input.script.parseP2shMultisigSpend();
        const txWithSignData = new Tx({
            version: this.version,
            inputs: this.inputs.map((inp, i) => i === inputIdx
                ? { ...copyTxInput(inp), signData }
                : copyTxInput(inp)),
            outputs: this.outputs,
            locktime: this.locktime,
        });
        const unsignedTx = unsignedTx_js_1.UnsignedTx.fromTx(txWithSignData);
        const inputAt = unsignedTx.inputAt(inputIdx);
        const sigHashType = sigHashType_js_1.SigHashType.fromInt((signature[signature.length - 1] ?? 0) & 0xff) ?? sigHashType_js_1.ALL_BIP143;
        const preimage = inputAt.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sigWithoutFlag = signature.slice(0, -1);
        let pubkeyIndex = -1;
        if (parsed.isSchnorr) {
            for (let i = 0; i < parsed.pubkeys.length; i++) {
                try {
                    ecc.schnorrVerify(sigWithoutFlag, sighash, parsed.pubkeys[i]);
                    pubkeyIndex = i;
                    break;
                }
                catch {
                    /* try next pubkey */
                }
            }
            if (pubkeyIndex < 0) {
                throw new Error('Schnorr signature does not verify for any pubkey in the multisig script');
            }
        }
        else {
            for (let i = 0; i < parsed.pubkeys.length; i++) {
                try {
                    ecc.ecdsaVerify(sigWithoutFlag, sighash, parsed.pubkeys[i]);
                    pubkeyIndex = i;
                    break;
                }
                catch {
                    /* try next pubkey */
                }
            }
            if (pubkeyIndex < 0) {
                throw new Error('ECDSA signature does not verify for any pubkey in the multisig script');
            }
        }
        const sigsByPubkey = Array(parsed.pubkeys.length).fill(undefined);
        if (parsed.isSchnorr) {
            const indices = parsed.pubkeyIndices;
            const sortedIndices = [...indices].sort((a, b) => a - b);
            for (let i = 0; i < parsed.signatures.length; i++) {
                const sig = parsed.signatures[i];
                if (sig !== undefined && i < sortedIndices.length) {
                    sigsByPubkey[sortedIndices[i]] = sig;
                }
            }
        }
        else {
            for (const sig of parsed.signatures) {
                if (sig === undefined)
                    continue;
                const sigNoFlag = sig.slice(0, -1);
                for (let i = 0; i < parsed.pubkeys.length; i++) {
                    try {
                        ecc.ecdsaVerify(sigNoFlag, sighash, parsed.pubkeys[i]);
                        sigsByPubkey[i] = sig;
                        break;
                    }
                    catch {
                        /* try next pubkey */
                    }
                }
            }
        }
        sigsByPubkey[pubkeyIndex] = signature;
        const nonNullSigs = sigsByPubkey.filter((s) => s !== undefined);
        const sigsForScript = nonNullSigs.length >= parsed.numSignatures
            ? nonNullSigs.slice(0, parsed.numSignatures)
            : [
                ...nonNullSigs,
                ...Array(parsed.numSignatures - nonNullSigs.length).fill(undefined),
            ];
        const redeemScript = !isBare && 'redeemScript' in parsed
            ? parsed.redeemScript
            : undefined;
        const newScriptSig = parsed.isSchnorr
            ? (() => {
                const signerIndices = new Set();
                for (let i = 0; i < parsed.pubkeys.length &&
                    signerIndices.size < parsed.numSignatures; i++) {
                    if (sigsByPubkey[i] !== undefined)
                        signerIndices.add(i);
                }
                return isBare
                    ? script_js_1.Script.multisigSpend({
                        signatures: sigsForScript,
                        pubkeyIndices: signerIndices,
                        numPubkeys: parsed.numPubkeys,
                    })
                    : script_js_1.Script.multisigSpend({
                        signatures: sigsForScript,
                        redeemScript,
                        pubkeyIndices: signerIndices,
                    });
            })()
            : script_js_1.Script.multisigSpend({
                signatures: sigsForScript,
                redeemScript,
            });
        const newInputs = this.inputs.map((inp, i) => i === inputIdx
            ? { ...copyTxInput(inp), script: newScriptSig }
            : copyTxInput(inp));
        return new Tx({
            version: this.version,
            inputs: newInputs,
            outputs: this.outputs,
            locktime: this.locktime,
        });
    }
    /**
     * Like {@link addMultisigSignature}, but computes the signature from a
     * secret key: BIP143 preimage (or legacy if `sigHashType` is legacy),
     * Schnorr for Schnorr-format multisig spends and ECDSA otherwise.
     */
    addMultisigSignatureFromKey(params) {
        const sigHashType = params.sigHashType ?? sigHashType_js_1.ALL_BIP143;
        const ecc = params.ecc ?? new ecc_js_1.Ecc();
        const { inputIdx, sk, signData } = params;
        const input = this.inputs[inputIdx];
        if (!input.script || input.script.bytecode.length === 0) {
            throw new Error(`Input ${inputIdx} has no scriptSig to add signature to`);
        }
        const isBare = signData.outputScript !== undefined &&
            signData.redeemScript === undefined;
        const parsed = isBare
            ? input.script.parseBareMultisigSpend(signData.outputScript)
            : input.script.parseP2shMultisigSpend();
        const txWithSignData = new Tx({
            version: this.version,
            inputs: this.inputs.map((inp, i) => i === inputIdx
                ? { ...copyTxInput(inp), signData }
                : copyTxInput(inp)),
            outputs: this.outputs,
            locktime: this.locktime,
        });
        const unsignedTx = unsignedTx_js_1.UnsignedTx.fromTx(txWithSignData);
        const preimage = unsignedTx
            .inputAt(inputIdx)
            .sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sig = parsed.isSchnorr
            ? ecc.schnorrSign(sk, sighash)
            : ecc.ecdsaSign(sk, sighash);
        const signature = (0, signatories_js_1.flagSignature)(sig, sigHashType);
        return this.addMultisigSignature({
            inputIdx,
            signature,
            signData,
            ecc,
        });
    }
    /**
     * Whether every **multisig** input (identified from `signData`) has enough
     * signatures in its scriptSig. Non-multisig inputs are ignored.
     *
     * If the transaction has **no** multisig inputs, this returns `true` (there
     * is nothing multisig-specific left to satisfy). That can look surprising on
     * a non-multisig or otherwise incomplete tx; this helper is **not** a
     * broadcast-readiness check. Call sites are expected to use it only in
     * multisig / PSBT flows where the question is specifically whether multisig
     * inputs still need more signatures (including mixed txs: non-multisig
     * inputs are finalized elsewhere).
     */
    isFullySignedMultisig() {
        for (let i = 0; i < this.inputs.length; i++) {
            const input = this.inputs[i];
            const multisigScript = input.signData?.redeemScript !== undefined
                ? input.signData.redeemScript
                : input.signData?.outputScript?.isMultisig()
                    ? input.signData.outputScript
                    : undefined;
            if (multisigScript === undefined) {
                continue;
            }
            if (!input.script || input.script.bytecode.length === 0) {
                return false;
            }
            try {
                const parsed = input.signData?.redeemScript === undefined
                    ? input.script.parseBareMultisigSpend(multisigScript)
                    : input.script.parseP2shMultisigSpend();
                const sigCount = parsed.signatures.filter(s => s !== undefined).length;
                if (sigCount < parsed.numSignatures) {
                    return false;
                }
            }
            catch {
                return false;
            }
        }
        return true;
    }
}
exports.Tx = Tx;
function readTxOutput(bytes) {
    const sats = bytes.readU64();
    const script = script_js_1.Script.readWithSize(bytes);
    return {
        sats,
        script,
    };
}
/** Write an outpoint to a Writer */
function writeOutPoint(outpoint, writer) {
    const txid = typeof outpoint.txid === 'string'
        ? (0, hex_js_1.fromHexRev)(outpoint.txid)
        : outpoint.txid;
    writer.putBytes(txid);
    writer.putU32(outpoint.outIdx);
}
/** Write a TxInput to a Writer */
function writeTxInput(input, writer) {
    writeOutPoint(input.prevOut, writer);
    (input.script ?? new script_js_1.Script()).writeWithSize(writer);
    writer.putU32(input.sequence ?? exports.DEFAULT_SEQUENCE);
}
/** Write a TxOutput to a Writer */
function writeTxOutput(output, writer) {
    writer.putU64(output.sats);
    output.script.writeWithSize(writer);
}
/** Create a deep copy of the TxInput */
function copyTxInput(input) {
    return {
        prevOut: {
            txid: typeof input.prevOut.txid === 'string'
                ? input.prevOut.txid
                : new Uint8Array(input.prevOut.txid),
            outIdx: input.prevOut.outIdx,
        },
        script: input.script?.copy(),
        sequence: input.sequence,
        signData: input.signData && {
            sats: input.signData.sats,
            outputScript: input.signData.outputScript?.copy(),
            redeemScript: input.signData.redeemScript?.copy(),
        },
    };
}
/** Create a deep copy of the TxOutput */
function copyTxOutput(output) {
    return {
        sats: output.sats,
        script: output.script.copy(),
    };
}
//# sourceMappingURL=tx.js.map